// src/app/services/user-data.service.ts
import { Injectable } from '@angular/core';
import {
  Firestore,
  doc,
  onSnapshot,
  DocumentReference,
  collection,
  query,
  where,
  Query,
  QuerySnapshot,
  DocumentData,
  Unsubscribe,
} from '@angular/fire/firestore';
import { Observable, BehaviorSubject, EMPTY, Subject } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { UserDocument } from '../interfaces/user-document.interface';
import { SearchResult } from '../interfaces/searchResult.interface';
import { Timestamp } from 'firebase/firestore';
import { AuthService } from './auth.service';
import { User } from '@angular/fire/auth';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  // Thêm centralized user data management
  private userDataSubject = new BehaviorSubject<UserDocument | null>(null);
  public userData$ = this.userDataSubject.asObservable();
  
  private destroy$ = new Subject<void>();
  private currentUserDataUnsubscribe: Unsubscribe | null = null;

  constructor(
    private firestore: Firestore,
    private authService: AuthService
  ) {
    this.initCentralizedUserDataListener();
  }

  private initCentralizedUserDataListener(): void {
    // Sử dụng userSubject thay vì user$ (vì AuthService chưa có user$)
    this.authService.userSubject.pipe(
      takeUntil(this.destroy$),
      switchMap((user: User | null) => {
        // Cleanup previous listener
        if (this.currentUserDataUnsubscribe) {
          this.currentUserDataUnsubscribe();
          this.currentUserDataUnsubscribe = null;
        }

        if (user) {
          const userDocRef = doc(this.firestore, `User/${user.uid}`);
          
          return new Observable<UserDocument | null>(subscriber => {
            this.currentUserDataUnsubscribe = onSnapshot(
              userDocRef,
              (snapshot) => {
                if (snapshot.exists()) {
                  const userData = { 
                    id: snapshot.id, 
                    ...snapshot.data() 
                  } as UserDocument;
                  subscriber.next(userData);
                } else {
                  console.log('User document not found');
                  subscriber.next(null);
                }
              },
              (error) => {
                console.error('Error fetching user data in realtime:', error);
                subscriber.next(null);
              }
            );

            // Return cleanup function
            return () => {
              if (this.currentUserDataUnsubscribe) {
                this.currentUserDataUnsubscribe();
                this.currentUserDataUnsubscribe = null;
              }
            };
          });
        } else {
          // User logged out, clear data
          return new Observable<UserDocument | null>(subscriber => {
            subscriber.next(null);
          });
        }
      })
    ).subscribe({
      next: (userData) => {
        this.userDataSubject.next(userData);
      },
      error: (error) => {
        console.error('DataService centralized user data error:', error);
        this.userDataSubject.next(null);
      }
    });
  }

  /**
   * Get current user data synchronously
   */
  getCurrentUserData(): UserDocument | null {
    return this.userDataSubject.value;
  }

  /**
   * Helper methods for common role checks
   */
  hasRole(role: string): boolean {
    const userData = this.getCurrentUserData();
    return userData?.role?.includes(role) || false;
  }

  isPharmacy(): boolean {
    return this.hasRole('pharmacy');
  }

  isCsyt(): boolean {
    return this.hasRole('csyt');
  }

  isPaymentCenter(): boolean {
    return this.hasRole('paymentcenter');
  }

  isMember(): boolean {
    const userData = this.getCurrentUserData();
    return (userData?.diamonds || 0) >= 5000;
  }

  /**
   * Listen to user document in real-time.
   * Auto-unsubscribe when observable is unsubscribed.
   * 
   * @deprecated Use centralized userData$ instead for better performance
   */
  listenUserData$(userDocRef: DocumentReference): Observable<UserDocument | null> {
    console.warn('listenUserData$ is deprecated. Use DataService.userData$ for centralized user data management.');
    
    return new Observable((observer) => {
      const unsubscribe = onSnapshot(
        userDocRef,
        (snapshot) => {
          if (snapshot.exists()) {
            observer.next(snapshot.data() as UserDocument);
          } else {
            console.log('User document not found');
            observer.next(null);
          }
        },
        (error) => {
          console.error('Error fetching user data in realtime:', error);
          observer.next(null);
        }
      );

      // Teardown logic
      return () => unsubscribe();
    });
  }

  /**
   * Cleanup method
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.currentUserDataUnsubscribe) {
      this.currentUserDataUnsubscribe();
    }
  }
}
